import { useState } from 'react'

import { fehlertext } from '../../api/client'
import { useMedienExport, useSettings } from '../../hooks/queries'
import { alsApp } from '../../lib/betriebsart'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { useTexte } from '../../texte'

/**
 * "Medien als ZIP" (#75): the server packs the photos, videos and voice
 * messages of the trip and hands back a link, like the book. The link
 * opens the file the same way as the book's link: in the app started from
 * the home screen, Safari opens and the file can be saved there.
 *
 * **Not fetched into the page.** The book is fetched and handed to the
 * share sheet; a ZIP of a trip can be gigabytes, and a fetch of that
 * size in a browser tab is a tab that dies. The link is the way.
 *
 * The places in the file names come from Apple Maps (the second exception
 * in `CLAUDE.md`) -- said here, in the sentence under the button, like on
 * every search that leaves this server.
 */
export function MedienExport({ tripId }: { tripId: number }) {
  const t = useTexte().buch
  const build = useMedienExport(tripId)
  const einstellungen = useSettings()
  const ortsnamen = einstellungen.data?.services?.place === true
  const [built, setBuilt] = useState<{ url: string; filename: string; bytes: number; files: number } | null>(null)

  async function start() {
    const started = performance.now()
    try {
      const result = await build.mutateAsync()
      notiere(`Medien: ${result.files} Dateien gepackt, ${groesse(result.bytes)}, ${result.lookups} Ortsanfragen, in ${((performance.now() - started) / 1000).toFixed(1)} s`)
      setBuilt(result)
    } catch (fehler) {
      notiere(`Medien: fehlgeschlagen nach ${((performance.now() - started) / 1000).toFixed(1)} s: ${fehlertext(fehler)}`)
    }
  }

  return (
    <span className="exportknoepfe" data-export="medien-bereich">
      {build.isError && (
        <span className="notice notice--knapp" role="alert">
          {fehlertext(build.error)}
        </span>
      )}
      {built !== null && (
        <span className="exportknoepfe__hinweis">
          <a href={`${built.url}?name=${encodeURIComponent(built.filename)}`} target="_blank" rel="noopener" data-export="medien-link">
            {t.medienOeffnen(built.filename, built.files, groesse(built.bytes))}
          </a>
          {alsApp() && ` ${t.appHinweis}`}
        </span>
      )}
      <button type="button" className="button button--small button--outline" onClick={() => void start()} disabled={build.isPending} data-export="medien">
        {build.isPending ? t.medienPackt : t.medienZip}
      </button>
      <span className="exportknoepfe__hinweis">{ortsnamen ? t.medienOrteApple : t.medienOrteOhne}</span>
    </span>
  )
}
